import React, { useCallback, useEffect, useState } from "react";
import { Button, Checkbox, Input, Modal, Tag } from "antd";
import { toast } from "react-toastify";
import { adminFetch, formatDate } from "./api";

const LABELS = { scripts: "Scripts", characters: "Characters", scenes: "Scenes", notes: "Notes", revisions: "Revisions", categories: "Production categories", elements: "Production elements",
  invitations: "Invitations", aiResults: "AI results", sceneStudioProjects: "Scene Studio projects", files: "Stored files", filesKeptForOthers: "Files kept (used by another account)",
  collaboratorAccessRemoved: "Access to others' scripts removed", supportRequests: "Support requests", securityLogs: "Sign-in / reset logs" };

// Admin → Account deletion: record a verified email request, watch the 14-day notice, then delete.
const AccountDeletion = () => {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [verified, setVerified] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminFetch("/admin/account-deletions").then((d) => setRows(d.requests || [])).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { await adminFetch("/admin/account-deletions", { method: "POST", body: { email, confirmVerified: verified } }); setEmail(""); setVerified(false); toast.success("Request recorded. Nothing has been deleted yet."); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancel = async (r) => { try { await adminFetch(`/admin/account-deletions/${r.id}/cancel`, { method: "POST" }); load(); } catch (e) { toast.error(e.message); } };
  const complete = async () => {
    setBusy(true);
    try { await adminFetch(`/admin/account-deletions/${confirm.id}/complete`, { method: "POST", body: { confirm: typed } }); toast.success("Account deleted."); setConfirm(null); setTyped(""); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-6">
      <div className="admin-card rounded-xl border p-4 lg:p-5">
        <h3 className="font-bold text-[16px] mb-1">New deletion request</h3>
        <p className="dashboard-muted text-[14px] mb-3">Record a request only after confirming it came from the account's own email address. Recording it deletes nothing: collaborators on shared scripts get 14 days' notice, and the owner can transfer scripts first. Aim to complete within 30 days.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Input className="sm:max-w-[320px]" placeholder="Account email" aria-label="Account email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Checkbox checked={verified} onChange={(e) => setVerified(e.target.checked)}>Request verified from this email address</Checkbox>
          <Button className="dashboard-primary" loading={busy} disabled={!email || !verified} onClick={create}>Record request</Button>
        </div>
      </div>
      {!rows.length && <p className="dashboard-muted">No deletion requests.</p>}
      {rows.map((r) => (
        <div key={r.id} className="admin-card rounded-xl border p-4 lg:p-5">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <strong className="break-all">{r.email}</strong>
            <Tag color={r.status === "COMPLETED" ? "green" : r.status === "CANCELLED" ? "default" : "orange"}>{r.status === "PENDING" ? "Open" : r.status === "COMPLETED" ? "Completed" : "Cancelled"}</Tag>
          </div>
          <p className="dashboard-muted text-[14px]">Verified {formatDate(r.verifiedAt)} · Notice ends {formatDate(r.noticeEndsAt)} · Complete by {formatDate(r.dueBy)}{r.completedAt ? ` · Completed ${formatDate(r.completedAt)}` : ""}</p>
          {(r.inventory || r.report) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[14px] mt-3">
              {Object.entries((r.inventory && r.inventory.counts) || r.report).map(([k, v]) => <div key={k}><span className="dashboard-muted">{LABELS[k] || k}:</span> {v}</div>)}
            </div>
          )}
          {r.inventory?.sharedScripts?.length > 0 && (
            <div className="mt-3 text-[14px]"><strong>Shared scripts (deleted unless transferred):</strong>
              <ul className="list-disc pl-5">{r.inventory.sharedScripts.map((s) => <li key={s.documentId}>{s.title} — {s.collaborators.map((c) => c.name).join(", ")}</li>)}</ul>
            </div>
          )}
          {r.blockers?.length > 0 && <ul className="mt-3 text-[14px] text-[#B45309] list-disc pl-5">{r.blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
          {r.status === "PENDING" && (
            <div className="flex gap-2 mt-4">
              <Button danger type="primary" disabled={!r.canComplete} onClick={() => { setTyped(""); setConfirm(r); }}>Delete account permanently</Button>
              <Button onClick={() => cancel(r)}>Cancel request</Button>
            </div>
          )}
        </div>
      ))}
      <Modal open={!!confirm} title="Delete this account permanently?" onCancel={() => setConfirm(null)} okText="Delete" okButtonProps={{ danger: true, disabled: typed !== "DELETE", loading: busy }} onOk={complete}>
        <p className="mb-3">This removes {confirm?.email}'s account, scripts, characters, notes, AI results and stored files. It cannot be undone. Only a de-identified record that the request was completed is kept.</p>
        <Input placeholder="Type DELETE" aria-label="Type DELETE to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </Modal>
    </div>
  );
};

export default AccountDeletion;
